import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteShell from "../components/layout/SiteShell";
import DarkSection from "../components/layout/DarkSection";
import DarkCard from "../components/layout/DarkCard";
import SEO from "../components/seo/SEO";
import HomeOrderSelector from "../components/home/HomeOrderSelector";
import BestPriceTrustSection from "../components/content/BestPriceTrustSection";
import { PLAIN_STARTER_PACK_OFFER } from "../lib/data";
import { lookupPlainBasePriceGbp } from "../lib/pricing";
import { scrollToSeaMossChoice } from "../lib/plainGoldCart";
import { HOME_CTA_DARK_OUTLINE, HOME_CTA_DARK_PRIMARY } from "../lib/homeCta";

const gbp = (n) => `£${n.toFixed(2)}`;

const PRICE_ROWS = [
  { label: "300ml pouch", sizeMl: 300, pack: "pouch" },
  { label: "300ml jar", sizeMl: 300, pack: "jar" },
  { label: "500ml pouch", sizeMl: 500, pack: "pouch" },
  { label: "500ml jar", sizeMl: 500, pack: "jar" },
  { label: "1L pouch", sizeMl: 1000, pack: "pouch" },
  { label: "1L jar", sizeMl: 1000, pack: "jar" },
];

const PRODUCT_CARDS = [
  {
    id: "gold",
    title: "Gold Sea Moss",
    blurb: "Smooth, simple, everyday sea moss gel. A clear starting point for most routines.",
    tag: "Everyday base",
    accent: "gold",
    action: { type: "base", base: "gold", label: "Choose Gold" },
  },
  {
    id: "purple",
    title: "Purple Sea Moss",
    blurb: "Richer colour, same fridge-ready gel. Priced as our deeper base.",
    tag: "Deeper colour",
    accent: "purple",
    action: { type: "base", base: "purple", label: "Choose Purple" },
  },
  {
    id: "mixins",
    title: "Mix-ins",
    blurb: "Educational pairings people commonly research — ginger, turmeric, cinnamon and more. Custom blends may return later.",
    tag: "Learn first",
    accent: "sage",
    action: { type: "link", to: "/mix-ins", label: "Explore Mix-Ins" },
  },
];

export default function ShopPage() {
  const location = useLocation();

  useEffect(() => {
    const id = (location.hash || "").replace("#", "");
    if (!id) return;
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [location.hash]);

  return (
    <SiteShell theme="dark">
      <SEO
        title="Shop Moss Matrix | Gold and Purple Sea Moss Gel"
        description="Shop Moss Matrix Gold and Purple Sea Moss Gel in the UK. Choose jar or pouch, pick your size, and order fresh fridge-ready gel direct from our official website."
        path="/shop"
        ogType="website"
      />

      <main className="overflow-x-hidden">
        <HomeOrderSelector headingLevel="h1" />

        <DarkSection ariaLabelledby="shop-products-heading">
          <h2 id="shop-products-heading" className="font-sans font-semibold text-3xl md:text-4xl text-ivory m-0">
            Gold, Purple, and mix-in ideas
          </h2>
          <p className="mt-3 text-sage max-w-2xl m-0 leading-relaxed">
            Start with plain Gold or Purple. Mix-ins stay educational for now so ordering stays simple and fresh.
          </p>
          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {PRODUCT_CARDS.map((card) => (
              <DarkCard key={card.id} className="flex flex-col">
                <span
                  className={`text-[10px] tracking-[0.2em] uppercase ${
                    card.accent === "purple" ? "text-violet" : card.accent === "gold" ? "text-gold" : "text-sage"
                  }`}
                >
                  {card.tag}
                </span>
                <h3 className="font-sans font-semibold text-2xl text-ivory mt-3 m-0">{card.title}</h3>
                <p className="mt-3 text-sm text-sage leading-relaxed flex-1 m-0">{card.blurb}</p>
                {card.action.type === "link" ? (
                  <Link to={card.action.to} className={`${HOME_CTA_DARK_OUTLINE} mt-6 self-start px-5 py-2.5 text-xs`}>
                    {card.action.label}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className={`${HOME_CTA_DARK_OUTLINE} mt-6 self-start px-5 py-2.5 text-xs`}
                    onClick={() => scrollToSeaMossChoice(card.action.base)}
                  >
                    {card.action.label}
                  </button>
                )}
              </DarkCard>
            ))}
          </div>
        </DarkSection>

        <DarkSection id="trial-pouches" ariaLabelledby="starter-heading" className="!pt-0">
          <DarkCard className="border border-gold/30">
            <h2 id="starter-heading" className="font-sans font-semibold text-2xl md:text-3xl text-ivory m-0">
              {PLAIN_STARTER_PACK_OFFER.title}
            </h2>
            <p className="mt-2 text-sm font-medium text-goldhover m-0">
              {PLAIN_STARTER_PACK_OFFER.subtitle} · {PLAIN_STARTER_PACK_OFFER.priceLabel}
            </p>
            <p className="mt-3 text-sm text-sage max-w-2xl leading-relaxed m-0">{PLAIN_STARTER_PACK_OFFER.description}</p>
          </DarkCard>
        </DarkSection>

        <DarkSection ariaLabelledby="sizes-heading">
          <h2 id="sizes-heading" className="font-sans font-semibold text-3xl md:text-4xl text-ivory m-0">
            Sizes and prices
          </h2>
          <p className="mt-3 text-sage max-w-2xl m-0 leading-relaxed">
            Plain gel prices. UK delivery is charged separately at checkout.
          </p>

          <div className="mt-8 overflow-x-auto rounded-[1.5rem] border border-white/14">
            <table className="w-full min-w-[520px] text-left border-collapse">
              <thead>
                <tr className="bg-panel/90 border-b border-white/12">
                  <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-sage font-medium">Size</th>
                  <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-gold font-medium">Gold</th>
                  <th className="px-5 py-4 text-[10px] tracking-[0.2em] uppercase text-violet font-medium">Purple</th>
                </tr>
              </thead>
              <tbody>
                {PRICE_ROWS.map((row) => (
                  <tr key={row.label} className="border-b border-white/10 last:border-0 bg-void/40">
                    <td className="px-5 py-4 text-sm text-ivory font-medium">{row.label}</td>
                    <td className="px-5 py-4 text-sm text-sage tabular-nums">
                      {gbp(lookupPlainBasePriceGbp("gold", row.sizeMl, row.pack))}
                    </td>
                    <td className="px-5 py-4 text-sm text-sage tabular-nums">
                      {gbp(lookupPlainBasePriceGbp("purple", row.sizeMl, row.pack))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DarkSection>

        <DarkSection className="!pt-0">
          <BestPriceTrustSection />
        </DarkSection>

        <DarkSection ariaLabelledby="mixins-teaser-heading">
          <DarkCard className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-xl">
              <h2 id="mixins-teaser-heading" className="font-sans font-semibold text-2xl md:text-3xl text-ivory m-0">
                Explore mix-in ideas
              </h2>
              <p className="mt-3 text-sm text-sage leading-relaxed m-0">
                See ginger, turmeric, cinnamon and other common ingredients people pair with sea moss.
              </p>
            </div>
            <Link to="/mix-ins" className={`${HOME_CTA_DARK_PRIMARY} shrink-0 px-7 py-3.5`}>
              Explore Mix-Ins
            </Link>
          </DarkCard>
        </DarkSection>
      </main>
    </SiteShell>
  );
}
